import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { cn } from '@mdvip/shared/lib/utils';
import { Link } from 'react-router-dom';

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const SHARED =
  '-my-3 inline-flex min-h-11 items-center gap-2 self-start text-sm font-medium text-foreground no-underline hover:text-foreground/70';

interface BackLinkBaseProps {
  label: string;
  className?: string;
}

/**
 * `href` and `onClick` are mutually exclusive, enforced by the union rather than by a comment: a
 * caller that passed both would silently get one of them ignored.
 */
export type BackLinkProps = BackLinkBaseProps &
  ({ href: string; onClick?: never } | { onClick: () => void; href?: never });

/**
 * BackLink — a plain, presentational "go back" control, in two forms.
 *
 * Given `href` it is a link to that destination, with all the affordances of one (middle-click,
 * open in new tab, a visible target in the status bar). Given `onClick` it is a button instead,
 * for the case where the destination isn't a fixed URL — a detail page returning the member to
 * whichever gallery page they actually came from has to call `navigate(-1)`, which no href can
 * express. It renders a `button`, not a link, because a control that runs script rather than
 * resolving a URL is not a link.
 *
 * Either way this component holds no navigation-history opinion of its own; the caller decides.
 */
// Not destructured: destructuring `href`/`onClick` up front discards the union's correlation
// between them, leaving `href` as `string | undefined` in the link branch.
export function BackLink(props: BackLinkProps) {
  const classes = cn(SHARED, FOCUS, props.className);
  const content = (
    <>
      <Icon name="chevronLeft" size="xs" />
      <span className="underline decoration-from-font underline-offset-2">{props.label}</span>
    </>
  );

  if (props.onClick) {
    return (
      <button type="button" onClick={props.onClick} data-slot="back-link" className={classes}>
        {content}
      </button>
    );
  }

  return (
    <Link to={props.href} data-slot="back-link" className={classes}>
      {content}
    </Link>
  );
}
